<template>
  <Header />
  <div class="title-container">
    <h1>Staff</h1>

    <div class="filter-container">
      <input type="text" v-model="searchQuery" placeholder="Search by Name" />
      <input type="text" id="projectFilter" v-model="licenseSearchQuery" :placeholder="`Search by ${$labels.licenseNo}`" />
      <input type="text" v-model="departmentSearchQuery" placeholder="Search by Department" />
      <input v-if="ENABLE_USER_BIRTH_AND_CATEGORY" type="text" id="dobFilter" v-model="dobSearchQuery" placeholder="Search by Date of Birth" />
      <input v-if="ENABLE_USER_BIRTH_AND_CATEGORY" type="text" id="pobirthFilter" v-model="pobirthSearchQuery" placeholder="Search by Place of Birth" />
      <button class="clear-filter-button" @click="clearFilters">Clear Filters</button>
    </div>
  </div>
  <div class="container-fluid-trainee">
    <vue-good-table :columns="columns" :rows="processedRows" :pagination-options="{
      enabled: true,
      perPage: perPage
    }" :search-options="{ enabled: false }" :sort-options="{ enabled: true }">
      <template #table-row="props">
        <!-- Actions -->
        <span v-if="props.column.field === 'roles_display'">
          <div v-for="role in props.row.roles_display" :key="role">
            {{ role }}
          </div>
        </span>
        <span v-else-if="props.column.field === 'actions'" class="center-icons">
          <font-awesome-icon icon="edit" class="edit-icon" title="Edit" @click="openEditUser(props.row.user_id)" />
          <font-awesome-icon icon="trash-alt" class="delete-icon" title="Delete"
            @click="deleteUser(props.row.user_id)" />
          <!-- Toggle Active -->
          <font-awesome-icon
            :icon="props.row.is_active ? 'user-check' : 'user-slash'"
            :class="props.row.is_active ? 'active-icon' : 'inactive-icon'"
            :title="props.row.is_active ? 'Deactivate User' : 'Activate User'"
            @click="toggleUserActive(props.row)"
          />
        </span>
        <!-- Default cell -->
        <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
    </vue-good-table>
  </div>
  <div>
    <button class="btn btn-primary rounded" id=addlogbookButton @click="openAddUser()">Add User</button>
  </div>
  <user-modal v-if="showUserModal" :mode="modalMode" :user="selectedUser" :users="users"
    @close="closeUserModal" @saved="fetchUsers" />
</template>

<script>
import Header from './Header.vue';
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';
import UserModal from './AddEditUserModal.vue';
import { ENABLE_USER_BIRTH_AND_CATEGORY } from '@/config/featureFlags';

export default {
  name: 'ViewUsers',
  components: {
    VueGoodTable,
    Header,
    UserModal,
  },

  data() {
    return {
      users: [],
      searchQuery: '',
      licenseSearchQuery: '',
      departmentSearchQuery: '',
      dobSearchQuery: '',
      pobirthSearchQuery: '',
      perPage: 10,
      filteredUsersLength: 0,
      showUserModal: false,
      modalMode: "add",   // or 'edit'
      selectedUser: null,
      columns: [
        { label: 'ID', field: 'user_id', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'First Name', field: 'first_name', sortable: true, thClass: 'vgt-first-name-column', tdClass: 'vgt-first-name-column' },
        { label: 'Last Name', field: 'last_name', sortable: true, thClass: 'vgt-last-name-column', tdClass: 'vgt-last-name-column' },
        { label: 'Email', field: 'email', sortable: true, thClass: 'vgt-email-column', tdClass: 'vgt-email-column' },
        { label: 'Roles', field: 'roles_display', sortable: false, thClass: 'vgt-role-column', tdClass: 'vgt-role-column' },
        // { label: 'Category', field: 'category', sortable: true, thClass: 'vgt-category-column', tdClass: 'vgt-category-column' },
        { label: this.$labels.licenseNo, field: 'license_no', sortable: true, thClass: 'vgt-uid-column', tdClass: 'vgt-uid-column' },
        { label: 'Department', field: 'department', sortable: true, thClass: 'vgt-department-column', tdClass: 'vgt-department-column' },
        { label: 'Date of Birth', field: 'date_of_birth', sortable: true, thClass: 'vgt-date-column', tdClass: 'vgt-date-column' },
        { label: 'Place of Birth', field: 'place_of_birth', sortable: true, thClass: 'vgt-date-column', tdClass: 'vgt-date-column' },
        { label: 'Actions', field: 'actions', sortable: false, thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' }
      ].filter(c => ENABLE_USER_BIRTH_AND_CATEGORY || !['date_of_birth', 'place_of_birth'].includes(c.field))
    };
  },
  computed: {
    ENABLE_USER_BIRTH_AND_CATEGORY() {
      return ENABLE_USER_BIRTH_AND_CATEGORY;
    },
    processedRows() {
      return this.users.filter((t) => {
        const name = `${t.first_name} ${t.last_name}`.toLowerCase();
        const nameMatch = name.includes(this.searchQuery.toLowerCase());

        const idMatch = t.user_id !== 2;

        const licenseMatch = (t.license_no || '').toLowerCase()
          .includes(this.licenseSearchQuery.toLowerCase());

        const departmentMatch = (t.department || '').toLowerCase()
          .includes(this.departmentSearchQuery.toLowerCase());

        const dobMatch = (t.date_of_birth || '').toLowerCase()
          .includes(this.dobSearchQuery.toLowerCase());

        const pobMatch = (t.place_of_birth || '').toLowerCase()
          .includes(this.pobirthSearchQuery.toLowerCase());

        return nameMatch && 
        idMatch && 
        departmentMatch && licenseMatch && dobMatch && pobMatch;
      });
    },
    uniqueRoles() {
      const all = this.users.flatMap(u => Array.isArray(u.roles) ? u.roles : []);

      return [...new Set(all)]
        .filter(Boolean)
        .map(role => this.getReadableRole(role));
    },
  },
  methods: {
    getReadableRole(role) {
      const roleMappings = {
        admin: 'Administrator',
        examiner: 'Examiner',
        practical_manager: 'Practical Element Manager',
        project_manager: 'Session Manager',
        instructor: 'Instructor',
        trainee: 'Trainee',
        assessor: 'Assessor',
      };
      return roleMappings[role] || role;
    },
    updateDropdown() {
      const col = this.columns.find(c => c.field === "roles_display");
      if (!col) return;

      col.filterOptions = {
        enabled: true,
        placeholder: "All roles",
        filterDropdownItems: this.uniqueRoles, // e.g. ["admin","manager","instructor"]
        filterFn: (rowValue, filterValue) => {
          if (!filterValue) return true;

          const roles = Array.isArray(rowValue)
            ? rowValue
            : String(rowValue || '').split(',');

          return roles
            .map(role => role.trim().toLowerCase())
            .includes(filterValue.toLowerCase());
        },
      };
    },
    async fetchUsers() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch all Users
        let result = await axios.get('/api/users', { headers });

        this.users = (result.data || [])
          .map(u => {
            const rolesArr = Array.isArray(u.roles) ? u.roles : [];
            return {
              ...u,
              roles: rolesArr,
              roles_display: rolesArr.map(role => this.getReadableRole(role)),
              has_trainee: rolesArr.includes('trainee'),
            };
          })
          .filter(u => !u.has_trainee);

        this.updateDropdown();

      } catch (error) {
        console.error('An error occurred while fetching data:', error);
      }
    },
    async deleteUser(id) {
      if (!confirm('Are you sure you want to delete this User?')) return;
      if (!confirm('This action will PERMANENTLY delete the User from the database. Do you wish to continue?')) return;

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const response = await axios.delete(`/api/users/${id}`, { headers });

        if (response.status === 200) {
          // await removeItemFromCacheByUrl(`/api/users/${id}`);
          this.users = this.users.filter(a => String(a.user_id) !== String(id));
          window.alert('User deleted successfully');
        }
      } catch (error) {
        if (error.isOffline === true && error.queued) {
          this.users = this.users.filter(a => String(a.userid) !== String(id));
          alert('You are offline. The delete has been queued and will sync later.');
          return;
        } else {
          alert('An error occurred while deleting.');
          console.log('error', error)
        }
      }
    },
    async toggleUserActive(user) {
      const newStatus = !user.is_active;

      const action = newStatus ? 'activate' : 'deactivate';

      if (!confirm(`Are you sure you want to ${action} this user?`)) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        await axios.put(
          `/api/users/${user.user_id}`,
          { is_active: newStatus },
          { headers }
        );

        // Update UI instantly
        user.is_active = newStatus;

      } catch (error) {
        console.error('Failed to update user status:', error);
        alert('Failed to update user status');
      }
    },
    openAddUser() {
      this.modalMode = "add";
      this.selectedUser = null;
      this.showUserModal = true;
    },
    openEditUser(user_id) {
      this.modalMode = "edit";
      this.selectedUser = this.users.find(c => c.user_id === user_id);
      this.showUserModal = true;
    },
    closeUserModal() {
      this.showUserModal = false;
      this.fetchUsers();
    },
    clearFilters() {
      // Clear all filter-related data properties
      this.searchQuery = '';
        this.licenseSearchQuery = '';
        this.departmentSearchQuery = '';
        this.dobSearchQuery = '';
        this.pobirthSearchQuery = '';
    },
  },
  async mounted() {
    this.fetchUsers();
  },
};
</script>


<style scoped>
.container-fluid-trainee {
  padding-top: 10px;
  width: 98%;
  margin-left: 1%;
  margin-right: 1%;
  display: flex;
  justify-content: center;
}

button.btn#addlogbookButton {
  padding: 10px 10px;
  font-size: 16px;
  width: 180px;
  height: 40px;
  margin-top: 0px;
  margin-bottom: 15px;
  margin-right: 20px;
  border: 1px solid rgb(0, 96, 134);
  background: #2952c4;
  color: white;
  cursor: pointer;
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
  float: right;
}

button.btn#addlogbookButton:hover {
  background-color: #0c1a61;
  transition: transform 0.2s, box-shadow 0.2s;
}

h1 {
  padding-top: 0px;
  padding-left: 3%;
}

label {
  margin-top: 8px;
}

.edit-icon {
  font-size: 16px;
  /* Adjust the size as needed */
  color: #615f5f;
  /* Change the color to your preference */
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
}

.edit-icon:hover {
  color: #2b2a2a;
  /* Change the color to your preference */
  cursor: pointer;
  transform: scale(1.1);
  /* Example transform for scaling on hover */

}

.delete-icon {
  font-size: 16px;
  color: #FF0000;
  /* Red color */
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
  padding-left: 15px;
}

.delete-icon:hover {
  color: #a82222;
  /* Lighter red color on hover */
  cursor: pointer;
  transform: scale(1.1);
  /* Scale the icon on hover */
  /* You can also add a box-shadow or any other styles you prefer */
}

input {
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 16px;
  width: 200px;
}

select {
  padding: 5px;
  border-radius: 5px;
  font-size: 16px;
  width: 200px;
}

.active-icon {
  color: #28a745;
  cursor: pointer;
  margin: 0 6px;
  padding-left: 10px;
}

.inactive-icon {
  color: #dc3545;
  cursor: pointer;
  margin: 0 6px;
  padding-left: 10px;
}
</style>